import { Autocomplete, TextField } from '@mui/material';
import { useRowSelectionContextOptional } from 'components/atoms/RowSelectionContext';
import { Permission } from 'interfaces/permissionOption';
import { find, isEmpty, map, omit, orderBy } from 'lodash';
import React from 'react';
import { useLabs } from 'utils/queryHooks/useLabs';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';

interface Props {
  responsiveWidth?: number;
}

const LabDropdown: React.FunctionComponent<React.PropsWithChildren<Props>> = ({ responsiveWidth = 260 }) => {
  const { data: labsRaw = [] } = useLabs();

  const { hasPermission } = usePermissions();
  const canViewMultipleLabs = hasPermission(Permission.ApplyPermissionsAcrossLabs);

  const { labId: currentLabId, setLabId } = useCurrentLabId();
  const { setQueryParams, queryParams } = useEncodedFilters();
  const rowSelection = useRowSelectionContextOptional();

  const labs = React.useMemo(() => orderBy(map(labsRaw, rawLabToLab), 'text'), [labsRaw]);
  const selectedLab = find(labs, { value: currentLabId }) || null;

  const handleLabClick = (_event: React.SyntheticEvent, lab: Lab | null) => {
    const labId = lab?.value;
    if (!labId) {
      return;
    }

    if (labId !== currentLabId) {
      setLabId(labId, 'replaceIn');
      // Remove studyId filter when switching labs
      setQueryParams({
        page: 1,
        filters:
          queryParams.filters?.studyId !== undefined
            ? isEmpty(omit(queryParams.filters, 'studyId'))
              ? undefined
              : omit(queryParams.filters, 'studyId')
            : queryParams.filters,
        slideIdsToInclude: [],
        caseIdsToInclude: [],
      });
      rowSelection?.clearSelection();
    }
  };

  return !isEmpty(labs) && canViewMultipleLabs ? (
    <Autocomplete<Lab, false, true, false>
      disableClearable
      options={labs}
      value={selectedLab}
      onChange={handleLabClick}
      getOptionLabel={(lab) => lab.text}
      isOptionEqualToValue={(lab, value) => lab.value === value.value}
      size="small"
      sx={{ width: responsiveWidth, minWidth: Math.min(responsiveWidth, 148), maxWidth: responsiveWidth }}
      renderInput={(params) => <TextField {...params} label="Lab" variant="outlined" />}
    />
  ) : null;
};

interface Lab {
  text: string;
  value: string;
}

const rawLabToLab = (rawLab: { name: string; id: string }): Lab => ({
  text: rawLab.name,
  value: rawLab.id,
});

export default LabDropdown;
